class HeaderBravoComponent extends HTMLElement {
  connectedCallback() {
    const toggleButtons = Array.from(this.querySelectorAll('[data-header-bravo-toggle]'));
    const panel = this.querySelector('[data-header-bravo-panel]');
    const section = this.closest('.header-bravo');
    const group = section?.closest('#header-group');
    const logoTemplate = section?.querySelector('[data-header-bravo-logo-template]');
    const accordionTriggers = Array.from(this.querySelectorAll('[data-header-bravo-accordion-trigger]'));
    const megaItems = Array.from(this.querySelectorAll('[data-header-bravo-mega-item]'));

    this.toggleButtons = toggleButtons.filter((button) => button instanceof HTMLElement);
    this.panel = panel instanceof HTMLElement ? panel : null;
    this.section = section instanceof HTMLElement ? section : null;
    this.group = group instanceof HTMLElement ? group : null;
    this.logoTemplate = logoTemplate instanceof HTMLTemplateElement ? logoTemplate : null;
    this.accordionTriggers = accordionTriggers.filter((trigger) => trigger instanceof HTMLElement);
    this.megaItems = megaItems.filter((item) => item instanceof HTMLElement);
    this.mobileStickyEnabled = this.section?.dataset.mobileSticky === 'true';
    this.activeMegaItem = null;

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleAccordionClick = this.handleAccordionClick.bind(this);
    this.handleMegaCloseClick = this.handleMegaCloseClick.bind(this);
    this.handleMegaTriggerClick = this.handleMegaTriggerClick.bind(this);
    this.handleMegaPanelClick = this.handleMegaPanelClick.bind(this);

    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', this.handleToggleClick);
    });

    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.handleAccordionClick);
    });

    this.megaItems.forEach((item) => {
      const panel = item.querySelector('[data-header-bravo-mega-panel]');
      const panelInner = item.querySelector('.header-bravo__mega-panel-inner');
      const trigger = item.querySelector('[data-header-bravo-mega-trigger]');

      if (trigger instanceof HTMLElement) {
        trigger.addEventListener('click', this.handleMegaTriggerClick);
      }

      if (panel instanceof HTMLElement) {
        panel.addEventListener('click', this.handleMegaPanelClick);
      }

      if (panelInner instanceof HTMLElement) {
        panelInner.addEventListener('click', this.handleMegaPanelClick);
      }

      const closeMark = item.querySelector('[data-header-bravo-mega-close]');

      if (closeMark instanceof HTMLElement) {
        closeMark.addEventListener('click', this.handleMegaCloseClick);
      }
    });

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('load', this.handleResize);

    this.syncGroupMode();
    this.mountLogoOverlay();
    this.syncAnnouncementOffset();
  }

  disconnectedCallback() {
    this.toggleButtons?.forEach((button) => {
      button.removeEventListener('click', this.handleToggleClick);
    });

    this.accordionTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleAccordionClick);
    });

    this.megaItems?.forEach((item) => {
      const trigger = item.querySelector('[data-header-bravo-mega-trigger]');
      const panel = item.querySelector('[data-header-bravo-mega-panel]');
      const panelInner = item.querySelector('.header-bravo__mega-panel-inner');
      const closeMark = item.querySelector('[data-header-bravo-mega-close]');

      if (trigger instanceof HTMLElement) {
        trigger.removeEventListener('click', this.handleMegaTriggerClick);
      }

      if (panel instanceof HTMLElement) {
        panel.removeEventListener('click', this.handleMegaPanelClick);
      }

      if (panelInner instanceof HTMLElement) {
        panelInner.removeEventListener('click', this.handleMegaPanelClick);
      }

      if (closeMark instanceof HTMLElement) {
        closeMark.removeEventListener('click', this.handleMegaCloseClick);
      }
    });

    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('load', this.handleResize);

    this.resetGroupMode();
    this.unmountLogoOverlay();
  }

  /** @param {MouseEvent} event */
  handleToggleClick(event) {
    if (this.toggleButtons.length === 0) {
      return;
    }

    const button = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const isOpen = button?.getAttribute('aria-expanded') === 'true';
    this.setOpenState(!isOpen);

  }

  /** @param {MouseEvent} event */
  handleAccordionClick(event) {
    const trigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (!trigger) return;

    const accordionItem = trigger.closest('.header-bravo__mobile-menu-item--accordion');
    const panel = accordionItem?.querySelector('[data-header-bravo-accordion-panel]');
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    this.accordionTriggers.forEach((otherTrigger) => {
      if (!(otherTrigger instanceof HTMLElement)) return;
      const otherItem = otherTrigger.closest('.header-bravo__mobile-menu-item--accordion');
      const otherPanel = otherItem?.querySelector('[data-header-bravo-accordion-panel]');
      otherTrigger.setAttribute('aria-expanded', 'false');
      this.syncAccordionMark(otherTrigger, false);
      if (otherPanel instanceof HTMLElement) {
        this.setAccordionPanelState(otherPanel, false);
      }
    });

    trigger.setAttribute('aria-expanded', String(!isOpen));
    this.syncAccordionMark(trigger, !isOpen);
    if (panel instanceof HTMLElement) {
      this.setAccordionPanelState(panel, !isOpen);
    }
  }

  /** @param {MouseEvent} event */
  handleDocumentClick(event) {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.setOpenState(false);
    this.setMegaItem(null);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.setOpenState(false);
      this.setMegaItem(null);
    }
  }

  handleResize() {
    this.syncAnnouncementOffset();

    if (window.innerWidth >= 750) {
      this.setOpenState(false);
    }

    if (window.innerWidth < 750) {
      this.setMegaItem(null);
    }
  }

  syncAnnouncementOffset() {
    if (!this.section) {
      return;
    }

    const group = this.section.closest('#header-group');

    if (!group) {
      this.section.style.setProperty('--header-bravo-announcement-offset', '0px');
      return;
    }

    let offset = 0;
    let sibling = this.section.parentElement?.previousElementSibling;

    while (sibling) {
      if (sibling instanceof HTMLElement) {
        offset += sibling.offsetHeight;
      }
      sibling = sibling.previousElementSibling;
    }

    this.section.style.setProperty('--header-bravo-announcement-offset', `${offset}px`);

    if (this.logoOverlay) {
      this.logoOverlay.style.setProperty('--header-bravo-announcement-offset', `${offset}px`);
    }
  }

  syncGroupMode() {
    if (!this.group) {
      return;
    }

    this.group.classList.toggle('header-group-bravo--mobile-sticky', this.mobileStickyEnabled);
  }

  resetGroupMode() {
    if (!this.group || !this.mobileStickyEnabled) {
      return;
    }

    this.group.classList.remove('header-group-bravo--mobile-sticky');
  }

  mountLogoOverlay() {
    if (!this.logoTemplate?.content.firstElementChild || !this.section?.dataset.sectionId) {
      return;
    }

    this.unmountLogoOverlay();

    const logoOverlay = this.logoTemplate.content.firstElementChild.cloneNode(true);

    if (!(logoOverlay instanceof HTMLElement)) {
      return;
    }

    this.logoOverlay = logoOverlay;
    document.body.append(logoOverlay);
  }

  unmountLogoOverlay() {
    if (!this.logoOverlay) {
      return;
    }

    this.logoOverlay.remove();
    this.logoOverlay = null;
  }

  /** @param {MouseEvent} event */
  handleMegaCloseClick(event) {
    const closeMark = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const item = closeMark?.closest('[data-header-bravo-mega-item]');

    if (!(item instanceof HTMLElement) || window.innerWidth < 750) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const isOpen = this.activeMegaItem === item;
    this.setMegaItem(isOpen ? null : item);
  }

  /** @param {MouseEvent} event */
  handleMegaTriggerClick(event) {
    const trigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const item = trigger?.closest('[data-header-bravo-mega-item]');

    if (!(item instanceof HTMLElement) || window.innerWidth < 750) {
      return;
    }

    event.preventDefault();
    const isOpen = this.activeMegaItem === item;
    this.setMegaItem(isOpen ? null : item);
  }

  /** @param {MouseEvent} event */
  handleMegaPanelClick(event) {
    const panel = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;

    if (!(panel instanceof HTMLElement) || window.innerWidth < 750 || event.target !== panel) {
      return;
    }

    this.setMegaItem(null);
  }

  /** @param {HTMLElement | null} item */
  setMegaItem(item) {
    this.activeMegaItem = item;
    const hasActiveMega = item instanceof HTMLElement && window.innerWidth >= 750;
    document.body.classList.toggle('header-bravo-mega-open', hasActiveMega);

    this.megaItems.forEach((megaItem) => {
      const trigger = megaItem.querySelector('[data-header-bravo-mega-trigger]');
      const panel = megaItem.querySelector('[data-header-bravo-mega-panel]');
      const isActive = megaItem === item && window.innerWidth >= 750;

      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', String(isActive));
      }

      if (panel instanceof HTMLElement) {
        panel.hidden = !isActive;
      }
    });
  }

  /** @param {HTMLElement} trigger @param {boolean} isOpen */
  syncAccordionMark(trigger, isOpen) {
    const mark = trigger.querySelector('.header-bravo__mobile-menu-toggle-mark');
    if (mark instanceof HTMLElement) {
      mark.textContent = isOpen ? '[ - ]' : '[ + ]';
    }
  }

  /** @param {HTMLElement} panel @param {boolean} isOpen */
  setAccordionPanelState(panel, isOpen) {
    clearTimeout(panel.headerBravoAccordionTimer);

    if (isOpen) {
      panel.hidden = false;
      panel.classList.add('is-opening');
      panel.classList.remove('is-closing');
      panel.style.height = '0px';

      requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      });

      panel.headerBravoAccordionTimer = setTimeout(() => {
        panel.classList.remove('is-opening');
        panel.style.height = 'auto';
      }, 260);
      return;
    }

    if (panel.hidden) {
      return;
    }

    panel.classList.remove('is-opening');
    panel.classList.add('is-closing');
    panel.style.height = `${panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      panel.style.height = '0px';
    });

    panel.headerBravoAccordionTimer = setTimeout(() => {
      panel.hidden = true;
      panel.classList.remove('is-closing');
      panel.style.height = '';
    }, 260);
  }

  /** @param {boolean} isOpen */
  setOpenState(isOpen) {
    if (this.toggleButtons.length === 0 || !this.panel) {
      return;
    }

    clearTimeout(this.mobilePanelCloseTimer);

    this.toggleButtons.forEach((button) => {
      button.setAttribute('aria-expanded', String(isOpen));
    });

    document.body.classList.remove('header-bravo-mobile-closing');

    if (isOpen) {
      this.panel.hidden = false;
      requestAnimationFrame(() => {
        document.body.classList.add('header-bravo-mobile-open');
      });
    } else {
      document.body.classList.remove('header-bravo-mobile-open');
      document.body.classList.add('header-bravo-mobile-closing');

      this.mobilePanelCloseTimer = setTimeout(() => {
        if (this.panel) {
          this.panel.hidden = true;
        }
        document.body.classList.remove('header-bravo-mobile-closing');
      }, 280);
    }

    if (!isOpen) {
      this.accordionTriggers.forEach((trigger) => {
        if (!(trigger instanceof HTMLElement)) return;
        const accordionItem = trigger.closest('.header-bravo__mobile-menu-item--accordion');
        const panel = accordionItem?.querySelector('[data-header-bravo-accordion-panel]');
        trigger.setAttribute('aria-expanded', 'false');
        this.syncAccordionMark(trigger, false);
        if (panel instanceof HTMLElement) {
          this.setAccordionPanelState(panel, false);
        }
      });
    }
  }
}

if (!customElements.get('header-bravo-component')) {
  customElements.define('header-bravo-component', HeaderBravoComponent);
}
